import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import {
  Wifi as WifiIcon,
  Computer as ComputerIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';

interface DeviceInfo {
  name: string;
  ip: string;
  location: string;
  tailscaleIP?: string;
}

const DeviceRegistration: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    name: '',
    ip: '',
    location: '',
    tailscaleIP: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const steps = [
    {
      label: '连接方式选择',
      description: '选择您的设备连接方式'
    },
    {
      label: '设备信息填写',
      description: '填写设备的基本信息'
    },
    {
      label: '连接测试',
      description: '测试设备连接是否正常'
    },
    {
      label: '完成注册',
      description: '设备注册完成'
    }
  ];

  const connectionMethods = [
    {
      id: 'tailscale',
      name: 'Tailscale (推荐)',
      description: '使用Tailscale VPN，自动发现设备，无需配置IP',
      icon: <WifiIcon />,
      color: 'primary'
    },
    {
      id: 'direct',
      name: '直接连接',
      description: '直接使用设备IP地址连接',
      icon: <ComputerIcon />,
      color: 'secondary'
    }
  ];

  const [selectedMethod, setSelectedMethod] = useState<string>('');

  const handleMethodSelect = (methodId: string) => {
    setSelectedMethod(methodId);
    setActiveStep(1);
  };

  const handleInputChange = (field: keyof DeviceInfo) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDeviceInfo(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleNext = () => {
    if (activeStep === 1) {
      // 验证必填字段
      if (!deviceInfo.name || !deviceInfo.ip || !deviceInfo.location) {
        setMessage({ type: 'error', text: '请填写所有必填字段' });
        return;
      }
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleTestConnection = async () => {
    setLoading(true);
    setMessage(null);

    try {
      // 🎯 调用后端API测试设备连接
      const response = await fetch(`${API_ENDPOINTS.DEVICES}/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ip: deviceInfo.ip
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setMessage({ type: 'success', text: `设备连接测试成功！${result.message || ''}` });
        setActiveStep(3);
      } else {
        throw new Error(result.message || result.error || '设备连接测试失败');
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: `设备连接测试失败: ${error.message || '请检查IP地址和网络连接'}` });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setLoading(true);
    setMessage(null);

    try {
      // 调用正确的设备注册API
      const response = await fetch(API_ENDPOINTS.DEVICES, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: deviceInfo.name,
          ip: deviceInfo.ip,
          location: deviceInfo.location
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ type: 'success', text: '设备注册成功！正在跳转到设备管理页面...' });
        console.log('设备注册成功:', result);
        
        // 3秒后跳转到设备管理页面
        setTimeout(() => {
          navigate('/devices');
        }, 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || '注册失败');
      }
    } catch (error) {
      console.error('设备注册失败:', error);
      setMessage({ type: 'error', text: `设备注册失败: ${error instanceof Error ? error.message : '请重试'}` });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              选择连接方式
            </Typography>
            <Grid container spacing={2}>
              {connectionMethods.map((method) => (
                <Grid item xs={12} md={6} key={method.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedMethod === method.id ? 2 : 1,
                      borderColor: selectedMethod === method.id ? 'primary.main' : 'grey.300',
                      '&:hover': {
                        borderColor: 'primary.main'
                      }
                    }}
                    onClick={() => handleMethodSelect(method.id)}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        {method.icon}
                        <Typography variant="h6" sx={{ ml: 1 }}>
                          {method.name}
                        </Typography>
                        {method.id === 'tailscale' && (
                          <Chip label={t('deviceRegistration.recommended')} color="primary" size="small" sx={{ ml: 1 }} />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {method.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              填写设备信息
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('deviceManager.deviceName')}
                  value={deviceInfo.name}
                  onChange={handleInputChange('name')}
                  placeholder={t('deviceRegistration.deviceNamePlaceholder')}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('deviceManager.deviceLocation')}
                  value={deviceInfo.location}
                  onChange={handleInputChange('location')}
                  placeholder={t('deviceRegistration.locationPlaceholder')}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('deviceManager.deviceIP')}
                  value={deviceInfo.ip}
                  onChange={handleInputChange('ip')}
                  placeholder={t('deviceRegistration.ipPlaceholder')}
                  required
                />
              </Grid>
              {selectedMethod === 'tailscale' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('deviceRegistration.tailscaleIP')}
                    value={deviceInfo.tailscaleIP}
                    onChange={handleInputChange('tailscaleIP')}
                    placeholder={t('deviceRegistration.tailscaleIPPlaceholder')}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              测试设备连接
            </Typography>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                设备信息：
              </Typography>
              <Typography variant="body1">名称：{deviceInfo.name}</Typography>
              <Typography variant="body1">IP：{deviceInfo.ip}</Typography>
              <Typography variant="body1">位置：{deviceInfo.location}</Typography>
              {deviceInfo.tailscaleIP && (
                <Typography variant="body1">Tailscale IP：{deviceInfo.tailscaleIP}</Typography>
              )}
            </Paper>
            <Button
              variant="contained"
              onClick={handleTestConnection}
              disabled={loading}
              startIcon={loading ? <CheckCircleIcon /> : <WifiIcon />}
            >
              {loading ? '测试中...' : '测试连接'}
            </Button>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              注册完成
            </Typography>
            <Box display="flex" alignItems="center" mb={2}>
              <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body1">
                设备已成功注册到系统中！
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              您现在可以：
            </Typography>
            <ul>
              <li>在设备管理页面查看您的设备</li>
              <li>执行自动化测试</li>
              <li>查看测试历史和报告</li>
              <li>与其他团队成员共享设备访问权限</li>
            </ul>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        设备注册
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        将您的Kiosk设备注册到自动化测试平台，实现远程管理和测试执行。
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {step.description}
                  </Typography>
                  {renderStepContent(index)}
                  <Box sx={{ mb: 2, mt: 2 }}>
                    {index < steps.length - 1 && (
                      <Button
                        variant="contained"
                        onClick={index === 2 ? handleRegister : handleNext}
                        disabled={loading}
                        sx={{ mr: 1 }}
                      >
                        {index === 2 ? '注册设备' : '下一步'}
                      </Button>
                    )}
                    {index > 0 && (
                      <Button onClick={handleBack} disabled={loading}>
                        上一步
                      </Button>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            连接方式说明
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <WifiIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Tailscale (推荐)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • 自动发现设备，无需手动配置IP<br/>
                  • 安全的VPN连接<br/>
                  • 支持跨网络访问<br/>
                  • 设备状态自动同步
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  直接连接
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • 需要设备固定IP地址<br/>
                  • 需要网络端口开放<br/>
                  • 适合内网环境<br/>
                  • 配置相对简单
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DeviceRegistration;
